import { useRef, useState, type ReactElement, type RefObject } from "react";
import { Link, useLocation } from "react-router";

import type { FieldErrors } from "../../api/client.js";
import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { ApiState, titleFor } from "../../components/api-state.js";

import { CenterBackLink } from "./center-back-link.js";
import { Pager } from "./pager.js";
import { PaymentSearchForm, type PaymentConditions, type PaymentResultChoice } from "./payment-search-form.js";
import { PaymentTabs } from "./payment-tabs.js";
import { adminCenterApi, adminCenterUrls } from "./urls.js";

/** 課金の結果の 1 行（PAY.JP との 1 回の通信。<b>文言はサーバが作る</b>——日時・操作・金額・結果・理由）。 */
export interface CenterPaymentRow {
    readonly createdLabel: string;
    /** 契約 ID。0 は新規申し込みの途中（契約の番号が決まる前）。 */
    readonly tenantId: number;
    /** 契約に紐づくか。<b>紐づかない行から契約を開かせない</b>（サーバが決める）。 */
    readonly hasTenant: boolean;
    readonly tenantTitle: string | null;
    readonly company: string | null;
    /** 操作した人のメールアドレス（記録したときのもの）。 */
    readonly email: string | null;
    readonly operationLabel: string;
    /** 定期課金の状態（試用中・有効・停止中・解除済み）。 */
    readonly subscriptionStatusLabel: string | null;
    readonly amountLabel: string | null;
    readonly resultLabel: string;
    /** 札の見た目（失敗は <c>badge-danger</c>）。 */
    readonly resultClass: string;
    readonly failureLabel: string | null;
    /** PAY.JP の側の ID（<c>cus_…</c>・<c>sub_…</c>）。 */
    readonly payjpId: string | null;
}

/** 課金の結果を探す画面（<c>GET /api/admin-center/payments</c>）。行は新しい順にサーバが並べる。 */
export interface CenterPayments extends PaymentConditions {
    /** 契約で絞ったときの、その契約の名前（サーバが引く）。絞っていない・無い契約なら null。 */
    readonly tenantTitle: string | null;
    readonly rows: readonly CenterPaymentRow[];
    readonly results: readonly PaymentResultChoice[];
    readonly page: number;
    readonly pageCount: number;
    readonly matchingCount: number;
}

const noErrors: FieldErrors = {};

/** 契約の欄。契約に紐づけば名前と企業名と契約 1 件の画面へのボタン、申し込みの途中ならその旨。 */
function Tenant({ row }: { readonly row: CenterPaymentRow }): ReactElement {
    if (!row.hasTenant) {
        return <span className="text-xs text-slate-500">申し込みの途中（契約の前）</span>;
    }
    return (
        <>
            {row.tenantTitle === null ? null : <span className="block font-medium text-ink-800">{row.tenantTitle}</span>}
            {row.company === null ? null : <span className="block text-xs text-slate-500">{row.company}</span>}
            <Link to={adminCenterUrls.tenant(row.tenantId)} className="btn-secondary btn-sm">{row.tenantId}</Link>
        </>
    );
}

function PaymentRows({ rows }: { readonly rows: readonly CenterPaymentRow[] }): ReactElement {
    return (
        <tbody>
            {rows.map((row, index) => (
                // 行を指す番号はサーバが返さない（並びは新しい順で決まる）
                <tr key={index}>
                    <td className="whitespace-nowrap text-slate-500">{row.createdLabel}</td>
                    <td><Tenant row={row} /></td>
                    <td>
                        <span className="block">{row.operationLabel}</span>
                        {row.subscriptionStatusLabel === null
                            ? null
                            : <span className="block text-xs text-slate-500">{row.subscriptionStatusLabel}</span>}
                        {row.email === null ? null : <span className="block text-xs text-slate-500">{row.email}</span>}
                    </td>
                    <td className="whitespace-nowrap">{row.amountLabel}</td>
                    <td className="whitespace-nowrap"><span className={row.resultClass}>{row.resultLabel}</span></td>
                    <td className="text-sm">{row.failureLabel}</td>
                    <td className="text-xs text-slate-500">{row.payjpId}</td>
                </tr>
            ))}
        </tbody>
    );
}

/** 契約で絞っていることを出す（契約の名前はサーバが返す。無い契約ならその旨）。 */
function TenantScope({ found }: { readonly found: CenterPayments }): ReactElement | null {
    if (found.tenantId === null) {
        return null;
    }
    const id = String(found.tenantId);
    return (
        <p className="mb-3 text-sm text-slate-500" data-payment-tenant="">
            {found.tenantTitle === null
                ? `契約 ID ${id} に絞っています（この番号の契約は見つかりません）。`
                : `契約「${found.tenantTitle}」（契約 ID ${id}）に絞っています。`}
        </p>
    );
}

function Results({ found, top }: {
    readonly found: CenterPayments;
    readonly top: RefObject<HTMLDivElement | null>;
}): ReactElement {
    if (found.rows.length === 0) {
        return <div className="empty-state text-sm text-slate-500">当たる記録がありません。</div>;
    }

    return (
        <>
            <p className="mb-3 text-sm text-slate-500">{found.matchingCount} 件</p>
            <div className="table-card" ref={top}>
                <div className="table-scroll">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th scope="col">日時</th>
                                <th scope="col">契約</th>
                                <th scope="col">操作</th>
                                <th scope="col">金額</th>
                                <th scope="col">結果</th>
                                <th scope="col">失敗の理由</th>
                                <th scope="col">PAY.JP の ID</th>
                            </tr>
                        </thead>
                        <PaymentRows rows={found.rows} />
                    </table>
                </div>
            </div>
            <Pager
                page={found.page}
                pageCount={found.pageCount}
                hasNext={found.page < found.pageCount}
                total={`全 ${String(found.matchingCount)} 件`}
                hrefOf={(page) => adminCenterUrls.payments({
                    from: found.from, to: found.to, result: found.result, keyword: found.keyword, tenantId: found.tenantId, page,
                })}
                top={top}
                marker={{ "data-payment-pager": "" }}
            />
        </>
    );
}

/** 断られた（400）ときの欄の値。<b>打った条件をそのまま残す</b>（直して探し直せる）。 */
export const conditionsOf = (search: string): PaymentConditions => {
    const query = new URLSearchParams(search);
    const raw = query.get("tenantId")?.trim() ?? "";
    const tenantId = Math.trunc(Number(raw));
    return {
        from: query.get("from"), to: query.get("to"), result: query.get("result"), keyword: query.get("keyword"),
        tenantId: raw === "" || Number.isNaN(tenantId) ? null : tenantId,
    };
};

/**
 * 課金の結果（PAY.JP とのやり取りの成功・失敗）。<b>読むだけ</b>——PAY.JP は呼ばず、ポータルが残した記録を並べる。
 * <b>条件は URL のクエリ</b>（<c>from</c>・<c>to</c>・<c>result</c>・<c>keyword</c>・<c>tenantId</c>・<c>page</c>）。
 * <c>tenantId</c> は契約 1 件の画面の入口から渡ってくる（その契約に絞る）。
 * 開けるかはサーバが決める（請求の権限。無ければ 404 →「見つからない」）。
 *
 * <b>期間・結果の誤りは 400（ValidationProblem）で断られる。</b>そのときも窓は残し、断りを欄の下（期間以外は先頭）に出す。
 * 結果の選択肢は最後に届いたものを使う。
 */
export function PaymentPage(): ReactElement | null {
    const { search } = useLocation();
    const data = useApiData<CenterPayments>(adminCenterApi.payments(search));
    usePageMeta(titleFor(data, "課金の結果"));
    const top = useRef<HTMLDivElement | null>(null);
    const [results, setResults] = useState<readonly PaymentResultChoice[]>([]);
    if (data.state === "ready" && data.value.results !== results) {
        setResults(data.value.results);
    }

    const rejected = data.state === "failed" && data.status === 400 && data.errors !== undefined;
    if (data.state !== "ready" && !rejected) {
        return <ApiState data={data} what="課金の結果" />;
    }

    return (
        <div className="page max-w-6xl">
            <CenterBackLink />
            <div className="page-header">
                <div>
                    <h1 className="page-title">課金の結果</h1>
                    <p className="page-subtitle">
                        ポータルが PAY.JP を呼んだとき（カードの登録・定期課金の作成と解除など）の成功・失敗を探します。
                        <strong className="text-ink-800">毎月の定期課金の請求の結果は出ません</strong>（「PAY.JP の課金（毎月の請求）」で確かめます）。
                    </p>
                </div>
            </div>
            <PaymentTabs current="payments" />

            {/* 窓は断られたときも同じ位置に描く（作り直すとフォーカスが外れる。docs/01 §8） */}
            {data.state === "ready"
                ? <PaymentSearchForm conditions={data.value} results={data.value.results} errors={noErrors} />
                : (
                    <PaymentSearchForm
                        conditions={conditionsOf(search)} results={results}
                        errors={data.errors ?? noErrors}
                    />
                )}
            {data.state === "ready" ? <TenantScope found={data.value} /> : null}
            {data.state === "ready" ? <Results found={data.value} top={top} /> : null}
        </div>
    );
}
